import type { TabsProps } from "antd";
import { Button, Skeleton, Tabs, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { AlertTriangle, Clock, Key, KeyRound, Link2, Shield, UserCircle } from "lucide-react";

import type { UserLoginLogVO } from "@/api/accounts/type";
import { EmptyBlock, SectionTitle } from "@/components/DetailBlocks";
import Modal from "@/components/Modal";
import { Table } from "@/components/Table";

import styles from "./AccountDetail.module.css";
import { AccountHeaderCard } from "./components/AccountHeaderCard";
import { BasicInfoTab } from "./components/BasicInfoTab";
import { BindInfoTab } from "./components/BindInfoTab";
import { RiskTab } from "./components/RiskTab";
import { RolesTab } from "./components/RolesTab";
import { useAccountDetail } from "./hooks/useAccountDetail";

/** 登录日志表格列（对齐 UserLoginLogVO） */
const loginLogColumns: ColumnsType<UserLoginLogVO> = [
  {
    title: "登录时间",
    dataIndex: "loginTime",
    width: 168,
    render: (v: string) => (
      <span className="text-xs font-mono">{v ? dayjs(v).format("YYYY-MM-DD HH:mm:ss") : "—"}</span>
    ),
  },
  {
    title: "方式",
    dataIndex: "loginMethod",
    render: (v: string) => <span className="text-xs">{v || "—"}</span>,
  },
  {
    title: "终端",
    dataIndex: "systemPoint",
    render: (v: string) =>
      v ? (
        <Tag bordered className="text-[10px] m-0">
          {v}
        </Tag>
      ) : (
        "—"
      ),
  },
  {
    title: "角色",
    dataIndex: "roleName",
    render: (v: string) => v || "—",
  },
  {
    title: "设备",
    dataIndex: "device",
    render: (v: string) => v || "—",
  },
  {
    title: "IP",
    dataIndex: "ipAddress",
    render: (v: string) => (v ? <span className="font-mono text-xs">{v}</span> : "—"),
  },
  {
    title: "位置",
    dataIndex: "location",
    render: (v: string) => v || "—",
  },
];

/** 详情 Modal props */
export interface AccountDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** 列表行数据，用于拉取详情 */
  id: string;
  /** 重置密码回调 */
  onResetPassword?: () => void;
  /** 权限管理回调 */
  onPermissionManage?: () => void;
}

/**
 * 账号详情 Modal
 * 结构：信息名片 + Tabs（基本 / 绑定 / 身份权限 / 风险 / 日志）
 */
export const AccountDetailModal = ({
  open,
  onClose,
  id,
  onResetPassword,
  onPermissionManage,
}: AccountDetailModalProps) => {
  const { detailTab, setDetailTab, accountDetail, loading, reloadAccountDetail } =
    useAccountDetail(id);
  const loginLogs = accountDetail?.loginLogs ?? [];

  const tabItems: TabsProps["items"] = [
    {
      key: "basic",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <UserCircle className="w-3.5 h-3.5" />
          账号信息
        </span>
      ),
      children: <BasicInfoTab accountDetail={accountDetail} />,
    },
    {
      key: "bind",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <Link2 className="w-3.5 h-3.5" />
          绑定信息
        </span>
      ),
      children: <BindInfoTab accountDetail={accountDetail} />,
    },
    {
      key: "roles",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          身份权限
        </span>
      ),
      children: (
        <RolesTab
          accountDetail={accountDetail}
          active={detailTab === "roles"}
          onStatusChanged={reloadAccountDetail}
        />
      ),
      className: styles.rolesTabPane,
    },
    {
      key: "risk",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          风险控制
        </span>
      ),
      children: <RiskTab accountDetail={accountDetail} />,
    },
    {
      key: "logs",
      label: (
        <span className="inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          登录日志
        </span>
      ),
      children: (
        <div>
          <SectionTitle
            icon={Clock}
            title="登录记录（仅显示近10条登录日志，完整日志请查看登录日志页面）"
          />
          {loginLogs.length === 0 ? (
            <EmptyBlock text="暂无登录记录" />
          ) : (
            <Table<UserLoginLogVO>
              size="small"
              fillHeight={false}
              rowKey="id"
              columns={loginLogColumns}
              dataSource={loginLogs}
              scroll={{ x: "max-content" }}
              pagination={false}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      centered
      title="账号详情"
      titleIcon={<Key className="w-5 h-5 text-orange-500" />}
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-slate-500">
            创建于
            {dayjs(accountDetail?.createAt).format("YYYY-MM-DD HH:mm:ss")}
          </span>
          <div className="flex items-center gap-2">
            {onResetPassword && (
              <Button icon={<KeyRound className="w-3.5 h-3.5" />} onClick={onResetPassword}>
                重置密码
              </Button>
            )}
            {onPermissionManage && (
              <Button icon={<Shield className="w-3.5 h-3.5" />} onClick={onPermissionManage}>
                权限管理
              </Button>
            )}
          </div>
        </div>
      }
      contentClassName="flex flex-1 flex-col min-h-0 overflow-hidden p-4"
      styles={{
        content: { height: "80vh" },
      }}
    >
      <div className={styles.bodyLayout}>
        <Skeleton loading={loading} active>
          <AccountHeaderCard accountDetail={accountDetail} />
        </Skeleton>
        <Tabs
          animated={{ inkBar: true, tabPane: false }}
          className={styles.detailTabs}
          style={{ flex: 1, minHeight: 0 }}
          activeKey={detailTab}
          onChange={setDetailTab}
          items={tabItems}
        />
      </div>
    </Modal>
  );
};
